/**
 * The content studio's provider contract — shared by the API that speaks it
 * and the settings screen that edits it.
 *
 * A connector is **data, not code**. Every provider differs in three places:
 * where the credential goes, what the request body looks like, and where in
 * the response the result is. Each of those is a field on the connector row,
 * so a provider nobody here has heard of is a row an operator fills in, not
 * a release. The catalogue at the bottom is only prefilled rows.
 *
 * The runtime never inspects a hostname. That was the mistake this replaces:
 * three providers in, each one had become its own `if`, and the fourth would
 * have been too.
 */
/** Where the connector's credential is placed on the outbound request. */
export const GenerationAuthStyle = {
    /** `Authorization: Bearer <token>` — OpenAI, OpenRouter, most REST APIs. */
    BEARER: 'BEARER',
    /** A named header, e.g. `x-goog-api-key` (Google), `xi-api-key` (ElevenLabs). */
    HEADER: 'HEADER',
    /** A named query parameter, e.g. `?key=` — the other form Google accepts. */
    QUERY: 'QUERY',
    /** No credential: an internal or already-authenticated endpoint. */
    NONE: 'NONE',
};
/** What `responsePath` points at, and therefore what to do with it. */
export const GenerationResponseKind = {
    /** A string (or an array of strings, joined) — a SCRIPT result. */
    TEXT: 'TEXT',
    /** An https URL this platform then fetches and stores. */
    URL: 'URL',
    /** Base64 bytes inline in the JSON. */
    BASE64: 'BASE64',
    /** The response body itself is the file (ElevenLabs streams audio this way); no JSON, no path. */
    BINARY: 'BINARY',
};
/**
 * Placeholders a request template (and the endpoint URL) may use.
 *
 * A string that is *exactly* one placeholder takes the value's own type, so
 * `"{{references}}"` becomes an array; a placeholder inside a longer string is
 * interpolated as text. A key whose rendered value is an empty string, an
 * empty array, or an empty `$each` is **dropped** from the body — providers
 * reject an empty `input_references` far more often than they ignore one.
 *
 * Repetition: `{ "$each": "references", "as": { …uses {{item}}… } }` renders
 * one copy of `as` per reference, which is how OpenRouter's
 * `[{ "type": "image_url", "image_url": { "url": … } }]` shape is expressed
 * without a provider-specific line of code.
 */
export const GenerationPlaceholder = {
    /** The idea, the script, or the image/video prompt. */
    PROMPT: 'prompt',
    /** The connector's `model` field — a model id, a voice id, whatever the provider calls it. */
    MODEL: 'model',
    /** Revision feedback; empty on a first attempt. */
    FEEDBACK: 'feedback',
    /** Signed link to the generated audio a VIDEO job should merge; empty when none. */
    AUDIO_URL: 'audioUrl',
    /** Signed links to the attached reference files (array). */
    REFERENCES: 'references',
    /** Inside a `$each` body only: the current element. */
    ITEM: 'item',
    /** In `pollUrlTemplate` only: the operation id read from the first reply. */
    OPERATION: 'operation',
    /**
     * The provider's own reference to the clip this VIDEO shot continues
     * (Veo extends only a video it generated, named by the URI it returned).
     * Empty for a first shot — so a key built on it is dropped, and one
     * template serves both the first clip and every extension.
     */
    PREVIOUS_VIDEO_URI: 'previousVideoUri',
    /**
     * The attached reference files as inline base64 (array of
     * `{ base64, mimeType }`), for providers that take bytes rather than a
     * link. Inside `$each` use `{{item.base64}}` and `{{item.mimeType}}`.
     * Images only, at most three, so a request never carries a video.
     */
    REFERENCES_INLINE: 'referencesInline',
    /** The first attached reference link, or empty — for an API that takes one image as a plain string (img_url). */
    FIRST_REFERENCE: 'firstReference',
    /**
     * The length this job asks for, in seconds — a video plan's shot length.
     * Empty for a job that names none, so `"duration": "{{duration}}"` is
     * dropped and the provider's own default applies.
     */
    DURATION: 'duration',
    /**
     * The aspect ratio the request chose (`9:16`), or empty when it chose none.
     * Dropped when empty, so the provider's own default applies.
     */
    ASPECT_RATIO: 'aspectRatio',
    /** The quality tier as a video resolution: DRAFT `480P`, STANDARD `720P`, HIGH `1080P`; empty when unset. */
    RESOLUTION: 'resolution',
    /** `WIDTHxHEIGHT` from the aspect ratio and quality tier (1:1 STANDARD is `1024x1024`); empty when no aspect was chosen. */
    SIZE: 'size',
    /** The provider's own voice id chosen for an audio request; empty when none. */
    VOICE: 'voice',
    /** Speaking speed, 1 = normal; empty when none was chosen. A number, so a JSON body gets a number. */
    SPEED: 'speed',
    /**
     * Whether the video should carry sound: false when the request asked for
     * none (or named no video options at all), true otherwise. A boolean is
     * never "empty", so `"audio": "{{audioEnabled}}"` is always sent.
     */
    AUDIO_ENABLED: 'audioEnabled',
};
const noPoll = {
    pollPath: null,
    pollUrlTemplate: null,
    pollDonePath: null,
    authOnResultFetch: false,
    extraHeaders: null,
    pollDoneValue: null,
    pollFailedValues: null,
};
/** Alibaba Model Studio (DashScope), Singapore / international. Every video and async image call needs this header. */
const DASHSCOPE = 'https://dashscope-intl.aliyuncs.com/api/v1';
const dashscopeAsync = {
    extraHeaders: { 'X-DashScope-Async': 'enable' },
    pollPath: 'output.task_id',
    pollUrlTemplate: `${DASHSCOPE}/tasks/{{operation}}`,
    pollDonePath: 'output.task_status',
    pollDoneValue: 'SUCCEEDED',
    pollFailedValues: 'FAILED,CANCELED,UNKNOWN',
    // The result is a signed OSS link; it carries its own authorisation.
    authOnResultFetch: false,
};
/** Model Studio's OpenAI-compatible chat endpoint (text models). */
const DASHSCOPE_COMPATIBLE = 'https://dashscope-intl.aliyuncs.com/compatible-mode/v1';
const geminiText = {
    contents: [{ parts: [{ text: '{{prompt}}' }] }],
};
/**
 * Prefilled rows. Editable after choosing — a preset is a starting point,
 * never a type the runtime branches on. Model names in particular go stale
 * quickly (two Google ids were retired within a single week of this being
 * built), so each one is a default an operator is expected to overwrite.
 */
export const GENERATION_PRESETS = [
    {
        id: 'mpr-script',
        label: 'This platform’s own script contract',
        capability: 'SCRIPT',
        provider: 'custom',
        endpointUrl: 'https://',
        authStyle: 'BEARER',
        authParamName: null,
        model: null,
        requestTemplate: {
            promptText: '{{prompt}}',
            referenceMediaUrls: '{{references}}',
            feedback: '{{feedback}}',
        },
        responsePath: 'text',
        responseKind: 'TEXT',
        responseMimePath: null,
        ...noPoll,
        note: 'POST { promptText, referenceMediaUrls, feedback } → { text }. For an endpoint you build yourself.',
    },
    {
        id: 'mpr-media',
        label: 'This platform’s own media contract',
        capability: 'IMAGE',
        provider: 'custom',
        endpointUrl: 'https://',
        authStyle: 'BEARER',
        authParamName: null,
        model: null,
        requestTemplate: { promptText: '{{prompt}}', audioMediaUrl: '{{audioUrl}}' },
        responsePath: 'resultUrl',
        responseKind: 'URL',
        responseMimePath: null,
        ...noPoll,
        note: 'POST { promptText, audioMediaUrl? } → { resultUrl }. Works for AUDIO, VIDEO and IMAGE.',
    },
    {
        id: 'google-gemini-text',
        label: 'Google Gemini — text (generateContent)',
        capability: 'SCRIPT',
        provider: 'Google Generative AI',
        endpointUrl: 'https://generativelanguage.googleapis.com/v1beta/models/{{model}}:generateContent',
        authStyle: 'HEADER',
        authParamName: 'x-goog-api-key',
        model: 'gemini-3.8-flash',
        requestTemplate: geminiText,
        responsePath: 'candidates[0].content.parts[*].text',
        responseKind: 'TEXT',
        responseMimePath: null,
        ...noPoll,
        usageInputTokensPath: 'usageMetadata.promptTokenCount',
        usageOutputTokensPath: 'usageMetadata.candidatesTokenCount',
        note: 'An AI Studio key goes in x-goog-api-key, never Authorization: Bearer. Google retires model ids often — check ListModels if this 404s.',
    },
    {
        id: 'google-interactions-image',
        label: 'Google Gemini — image (Interactions API)',
        capability: 'IMAGE',
        provider: 'Google Generative AI',
        endpointUrl: 'https://generativelanguage.googleapis.com/v1beta/interactions',
        authStyle: 'HEADER',
        authParamName: 'x-goog-api-key',
        model: 'gemini-3.1-flash-image',
        requestTemplate: { model: '{{model}}', input: [{ type: 'text', text: '{{prompt}}' }] },
        responsePath: 'steps[*].content[*].data',
        responseKind: 'BASE64',
        responseMimePath: 'steps[*].content[*].mime_type',
        ...noPoll,
        note: 'The image is the first data part across the reply’s steps (a “thought” step may come first). Not on Google’s free tier — billing must be enabled on the key’s project.',
    },
    {
        id: 'google-gemini-tts',
        label: 'Google Gemini — speech from text (TTS)',
        capability: 'AUDIO',
        provider: 'Google Generative AI',
        endpointUrl: 'https://generativelanguage.googleapis.com/v1beta/models/{{model}}:generateContent',
        authStyle: 'HEADER',
        authParamName: 'x-goog-api-key',
        model: 'gemini-3.8-flash-tts',
        requestTemplate: {
            contents: [{ parts: [{ text: '{{prompt}}' }] }],
            generationConfig: {
                responseModalities: ['AUDIO'],
                speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } } },
            },
        },
        responsePath: 'candidates[0].content.parts[*].inlineData.data',
        responseKind: 'BASE64',
        responseMimePath: 'candidates[0].content.parts[*].inlineData.mimeType',
        ...noPoll,
        note: 'Returns a ready WAV. Arabic works; steer tone by starting the script with an instruction (“قل بصوت هادئ:”). Change voiceName in the template for another voice (Kore, Puck, Charon, Aoede…).',
    },
    {
        id: 'google-veo-video',
        label: 'Google Veo — video (polls until done)',
        capability: 'VIDEO',
        provider: 'Google Generative AI',
        endpointUrl: 'https://generativelanguage.googleapis.com/v1beta/models/{{model}}:predictLongRunning',
        authStyle: 'HEADER',
        authParamName: 'x-goog-api-key',
        model: 'veo-3.1-fast-generate-preview',
        requestTemplate: {
            instances: [
                {
                    prompt: '{{prompt}}',
                    // Empty on a first shot, so the key is dropped; on an extension it
                    // names the clip Veo generated last time.
                    video: { uri: '{{previousVideoUri}}' },
                    referenceImages: {
                        $each: 'referencesInline',
                        as: { image: { bytesBase64Encoded: '{{item.base64}}', mimeType: '{{item.mimeType}}' }, referenceType: 'ASSET' },
                    },
                },
            ],
            // Extensions and reference images both require 8; a plan never needs another length.
            // Veo makes 16:9 and 9:16 only; with no aspect chosen the key is dropped and Veo's 16:9 applies.
            parameters: { aspectRatio: '{{aspectRatio}}', durationSeconds: 8 },
        },
        responsePath: 'response.generateVideoResponse.generatedSamples[0].video.uri',
        responseKind: 'URL',
        responseMimePath: null,
        pollPath: 'name',
        pollUrlTemplate: 'https://generativelanguage.googleapis.com/v1beta/{{operation}}',
        pollDonePath: 'done',
        authOnResultFetch: true,
        extraHeaders: null,
        pollDoneValue: null,
        pollFailedValues: null,
        slot: { videoInputs: ['TEXT', 'IMAGE'], aspectRatios: ['16:9', '9:16'], maxClipSeconds: 8, supportsExtension: true },
        note: 'Starts an operation, polls it until done (a few minutes), then downloads the file with the key. One template serves a first 8-second clip, a clip that extends the previous one (+7 s, up to 148 s total, via the studio’s video plan), and up to three reference images. Paid model. Veo generates its own sound; the studio’s audio is not merged.',
    },
    {
        id: 'google-imagen-predict',
        label: 'Google Imagen — image (:predict)',
        capability: 'IMAGE',
        provider: 'Google Generative AI',
        endpointUrl: 'https://generativelanguage.googleapis.com/v1beta/models/{{model}}:predict',
        authStyle: 'HEADER',
        authParamName: 'x-goog-api-key',
        model: 'imagen-4.0-generate-001',
        requestTemplate: {
            instances: [{ prompt: '{{prompt}}' }],
            parameters: { sampleCount: 1, aspectRatio: '{{aspectRatio}}' },
        },
        responsePath: 'predictions[0].bytesBase64Encoded',
        responseKind: 'BASE64',
        responseMimePath: 'predictions[0].mimeType',
        ...noPoll,
        slot: { aspectRatios: ['1:1', '3:4', '4:3', '9:16', '16:9'] },
        note: 'Only for an Imagen model id that ListModels says supports predict; older ids (imagen-3.0-*) were retired.',
    },
    {
        id: 'openrouter-text',
        label: 'OpenRouter — text (chat completions)',
        capability: 'SCRIPT',
        provider: 'OpenRouter',
        endpointUrl: 'https://openrouter.ai/api/v1/chat/completions',
        authStyle: 'BEARER',
        authParamName: null,
        model: 'google/gemini-2.5-flash',
        requestTemplate: { model: '{{model}}', messages: [{ role: 'user', content: '{{prompt}}' }] },
        responsePath: 'choices[0].message.content',
        responseKind: 'TEXT',
        responseMimePath: null,
        ...noPoll,
        usageInputTokensPath: 'usage.prompt_tokens',
        usageOutputTokensPath: 'usage.completion_tokens',
        note: 'Any chat model on openrouter.ai; the id is exactly as their model page shows it.',
    },
    {
        id: 'openrouter-image',
        label: 'OpenRouter — image',
        capability: 'IMAGE',
        provider: 'OpenRouter',
        endpointUrl: 'https://openrouter.ai/api/v1/images',
        authStyle: 'BEARER',
        authParamName: null,
        model: 'bytedance-seed/seedream-4.5',
        requestTemplate: {
            model: '{{model}}',
            prompt: '{{prompt}}',
            input_references: {
                $each: 'references',
                as: { type: 'image_url', image_url: { url: '{{item}}' } },
            },
        },
        responsePath: 'data[0].b64_json',
        responseKind: 'BASE64',
        responseMimePath: 'data[0].media_type',
        ...noPoll,
        note: 'Attached reference files are sent as input_references. A “styles” model (recraft/recraft-v4-styles) requires at least one; do not send n.',
    },
    {
        id: 'openai-image',
        label: 'OpenAI — image (gpt-image)',
        capability: 'IMAGE',
        provider: 'OpenAI',
        endpointUrl: 'https://api.openai.com/v1/images/generations',
        authStyle: 'BEARER',
        authParamName: null,
        model: 'gpt-image-1',
        requestTemplate: { model: '{{model}}', prompt: '{{prompt}}' },
        responsePath: 'data[0].b64_json',
        responseKind: 'BASE64',
        responseMimePath: null,
        ...noPoll,
        note: 'gpt-image models always return b64_json.',
    },
    {
        id: 'alibaba-wan3-video',
        label: 'Alibaba Wan 3.0 — video up to 30 s (with sound)',
        capability: 'VIDEO',
        provider: 'Alibaba Model Studio',
        endpointUrl: `${DASHSCOPE}/services/aigc/video-generation/video-synthesis`,
        authStyle: 'BEARER',
        authParamName: null,
        model: 'wan3.0-video',
        requestTemplate: {
            model: '{{model}}',
            input: {
                prompt: '{{prompt}}',
                media: { $each: 'references', as: { type: 'reference_image', url: '{{item}}' } },
            },
            parameters: { resolution: '{{resolution}}', ratio: '{{aspectRatio}}', duration: '{{duration}}', prompt_extend: true },
        },
        responsePath: 'output.video_url',
        responseKind: 'URL',
        responseMimePath: null,
        ...dashscopeAsync,
        slot: { videoInputs: ['TEXT', 'IMAGE'], maxClipSeconds: 30 },
        pricing: { unit: 'SECOND', byResolution: { '480P': 0.05, '720P': 0.1, '1080P': 0.2 }, currency: 'USD' },
        usageSecondsPath: 'usage.duration',
        note: 'One call makes up to 30 s, so a video plan up to 30 s can be a single shot. Billed per second of output: 480P $0.05, 720P $0.10, 1080P $0.20 (wan3.0-video-prime is the faster, dearer variant). Attached images go as reference_image. Key from Model Studio, Singapore region.',
    },
    {
        id: 'alibaba-wan26-i2v-flash',
        label: 'Alibaba Wan 2.6 flash — animate an image (cheapest)',
        capability: 'VIDEO',
        provider: 'Alibaba Model Studio',
        endpointUrl: `${DASHSCOPE}/services/aigc/video-generation/video-synthesis`,
        authStyle: 'BEARER',
        authParamName: null,
        model: 'wan2.6-i2v-flash',
        requestTemplate: {
            model: '{{model}}',
            input: { prompt: '{{prompt}}', img_url: '{{firstReference}}' },
            parameters: { resolution: '{{resolution}}', duration: '{{duration}}', audio: '{{audioEnabled}}' },
        },
        responsePath: 'output.video_url',
        responseKind: 'URL',
        responseMimePath: null,
        ...dashscopeAsync,
        slot: { videoInputs: ['IMAGE'], maxClipSeconds: 15 },
        pricing: { unit: 'SECOND', price: 0.025, soundPrice: 0.05, currency: 'USD' },
        usageSecondsPath: 'usage.duration',
        note: 'Animates the FIRST attached image (required), 2–15 s per call. Resolution follows the request’s quality (none chosen: the provider’s own default). Sound is generated only when the request asks for it (silent is $0.025/s at 720P, with sound $0.05/s); a job with no video options is silent.',
    },
    {
        id: 'alibaba-wan27-image',
        label: 'Alibaba Wan 2.7 — image',
        capability: 'IMAGE',
        provider: 'Alibaba Model Studio',
        endpointUrl: `${DASHSCOPE}/services/aigc/multimodal-generation/generation`,
        authStyle: 'BEARER',
        authParamName: null,
        model: 'wan2.7-image',
        requestTemplate: {
            model: '{{model}}',
            input: {
                messages: [
                    {
                        role: 'user',
                        content: [{ $each: 'references', as: { image: '{{item}}' } }, { text: '{{prompt}}' }],
                    },
                ],
            },
            parameters: { size: '2K', n: 1, watermark: false },
        },
        responsePath: 'output.choices[0].message.content[*].image',
        responseKind: 'URL',
        responseMimePath: null,
        ...noPoll,
        pricing: { unit: 'IMAGE', price: 0.03, currency: 'USD' },
        note: '$0.03 per image (wan2.7-image-pro: $0.075, up to 4K). Attached images are used as references for editing or composition. Answers directly — no polling.',
    },
    {
        id: 'alibaba-wan3-prime-video',
        label: 'Alibaba Wan 3.0 Prime — faster video up to 30 s (with sound)',
        capability: 'VIDEO',
        provider: 'Alibaba Model Studio',
        endpointUrl: `${DASHSCOPE}/services/aigc/video-generation/video-synthesis`,
        authStyle: 'BEARER',
        authParamName: null,
        model: 'wan3.0-video-prime',
        requestTemplate: {
            model: '{{model}}',
            input: {
                prompt: '{{prompt}}',
                media: { $each: 'references', as: { type: 'reference_image', url: '{{item}}' } },
            },
            parameters: { resolution: '{{resolution}}', ratio: '{{aspectRatio}}', duration: '{{duration}}', prompt_extend: true },
        },
        responsePath: 'output.video_url',
        responseKind: 'URL',
        responseMimePath: null,
        ...dashscopeAsync,
        slot: { videoInputs: ['TEXT', 'IMAGE'], maxClipSeconds: 30 },
        pricing: { unit: 'SECOND', byResolution: { '480P': 0.068, '720P': 0.14, '1080P': 0.28 }, currency: 'USD' },
        usageSecondsPath: 'usage.duration',
        note: 'The faster, dearer variant of Wan 3.0 (same request): billed per second of output, 480P $0.068, 720P $0.14, 1080P $0.28.',
    },
    {
        id: 'alibaba-qwen-max-text',
        label: 'Alibaba Qwen Max — text (chat completions)',
        capability: 'SCRIPT',
        provider: 'Alibaba Model Studio',
        endpointUrl: `${DASHSCOPE_COMPATIBLE}/chat/completions`,
        authStyle: 'BEARER',
        authParamName: null,
        model: 'qwen3.8-max',
        requestTemplate: { model: '{{model}}', messages: [{ role: 'user', content: '{{prompt}}' }] },
        responsePath: 'choices[0].message.content',
        responseKind: 'TEXT',
        responseMimePath: null,
        ...noPoll,
        pricing: { unit: 'TOKENS_1M', inputPrice: 2, outputPrice: 6, currency: 'USD' },
        usageInputTokensPath: 'usage.prompt_tokens',
        usageOutputTokensPath: 'usage.completion_tokens',
        note: 'Model Studio’s OpenAI-compatible mode, Singapore key. $2 per million input tokens, $6 per million output. Check the model id on Model Studio if this 404s.',
    },
    {
        id: 'alibaba-qwen-flash-text',
        label: 'Alibaba Qwen Flash — text (chat completions, cheap)',
        capability: 'SCRIPT',
        provider: 'Alibaba Model Studio',
        endpointUrl: `${DASHSCOPE_COMPATIBLE}/chat/completions`,
        authStyle: 'BEARER',
        authParamName: null,
        model: 'qwen3.8-flash',
        requestTemplate: { model: '{{model}}', messages: [{ role: 'user', content: '{{prompt}}' }] },
        responsePath: 'choices[0].message.content',
        responseKind: 'TEXT',
        responseMimePath: null,
        ...noPoll,
        pricing: { unit: 'TOKENS_1M', inputPrice: 0.15, outputPrice: 0.47, currency: 'USD' },
        usageInputTokensPath: 'usage.prompt_tokens',
        usageOutputTokensPath: 'usage.completion_tokens',
        note: 'The cheap Qwen text model: $0.15 per million input tokens, $0.47 per million output. Check the model id on Model Studio if this 404s.',
    },
    {
        id: 'huggingface-text',
        label: 'Hugging Face — text generation',
        capability: 'SCRIPT',
        provider: 'Hugging Face',
        endpointUrl: 'https://router.huggingface.co/hf-inference/models/{{model}}',
        authStyle: 'BEARER',
        authParamName: null,
        model: '',
        requestTemplate: { inputs: '{{prompt}}' },
        responsePath: '[0].generated_text',
        responseKind: 'TEXT',
        responseMimePath: null,
        ...noPoll,
        note: 'Only models Hugging Face actually serves for inference work; the old api-inference.huggingface.co host is being retired in favour of router.huggingface.co.',
    },
    {
        id: 'huggingface-media',
        label: 'Hugging Face — image or audio (raw bytes)',
        capability: 'IMAGE',
        provider: 'Hugging Face',
        endpointUrl: 'https://router.huggingface.co/hf-inference/models/{{model}}',
        authStyle: 'BEARER',
        authParamName: null,
        model: '',
        requestTemplate: { inputs: '{{prompt}}' },
        responsePath: null,
        responseKind: 'BINARY',
        responseMimePath: null,
        ...noPoll,
        note: 'Text-to-image and text-to-speech models answer with the file itself. Check the model’s task: a speech-recognition model (wav2vec2, whisper) turns audio into text and cannot generate audio.',
    },
    {
        id: 'elevenlabs-tts',
        label: 'ElevenLabs — speech from text',
        capability: 'AUDIO',
        provider: 'ElevenLabs',
        endpointUrl: 'https://api.elevenlabs.io/v1/text-to-speech/{{model}}',
        authStyle: 'HEADER',
        authParamName: 'xi-api-key',
        model: '',
        requestTemplate: { text: '{{prompt}}', model_id: 'eleven_multilingual_v2' },
        responsePath: null,
        responseKind: 'BINARY',
        responseMimePath: null,
        ...noPoll,
        note: '“Model” here is the voice id (Voices → copy id). The response is the MP3 itself. Arabic and Gulf dialects work best with a cloned Saudi voice.',
    },
];
export function findGenerationPreset(id) {
    return GENERATION_PRESETS.find((preset) => preset.id === id);
}
//# sourceMappingURL=generation.js.map